/**
 * <AuthImage> — renders a protected image.
 *
 * Plain <img src> can't send the Authorization header our file endpoints
 * require, so this fetches the bytes as a Blob through the axios client
 * (auth attached by interceptor), then renders an object URL. The Blob is
 * cached by TanStack Query; the object URL is created per-mount and
 * revoked on unmount.
 */
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ImageOff, Loader2 } from 'lucide-react';
import { api } from '@/lib/api';

async function fetch_blob(path: string): Promise<Blob> {
  const res = await api.get<Blob>(path, { responseType: 'blob' });
  return res.data;
}

export function use_file_blob(path: string | null) {
  return useQuery({
    queryKey: ['file-blob', path],
    queryFn: () => fetch_blob(path as string),
    enabled: path !== null,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
}

const EXT_BY_MIME: Record<string, string> = {
  'image/png': 'png',
  'image/jpeg': 'jpg',
  'image/webp': 'webp',
  'application/pdf': 'pdf',
};

/** Fetch a protected file and open it in a new tab (full-size view). */
export async function open_file_in_new_tab(path: string): Promise<void> {
  const blob = await fetch_blob(path);
  const url = URL.createObjectURL(blob);
  // NOTE: do NOT pass 'noopener' here — Chromium opens a blank tab for a blob:
  // URL when the new context has no opener (it can't resolve the blob), so the
  // full-size preview would silently fail.
  window.open(url, '_blank');
  // Give the new tab time to load the blob before revoking.
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

/**
 * Fetch a protected file and save it to disk. The extension is derived from the
 * blob's MIME type; `base_name` is the filename without extension.
 */
export async function download_file(path: string, base_name: string): Promise<void> {
  const blob = await fetch_blob(path);
  const ext = EXT_BY_MIME[blob.type] ?? 'jpg';
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${base_name}.${ext}`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export function AuthImage({
  path,
  alt,
  className,
  'data-testid': testid,
}: {
  path: string;
  alt: string;
  className?: string;
  'data-testid'?: string;
}) {
  const blob_q = use_file_blob(path);
  const [object_url, set_object_url] = useState<string | null>(null);

  useEffect(() => {
    if (!blob_q.data) return;
    const url = URL.createObjectURL(blob_q.data);
    set_object_url(url);
    return () => URL.revokeObjectURL(url);
  }, [blob_q.data]);

  if (blob_q.isError) {
    return (
      <div
        className={`flex items-center justify-center bg-muted text-muted-foreground ${className ?? ''}`}
        data-testid={testid}
        title="Preview unavailable"
      >
        <ImageOff className="h-5 w-5" />
      </div>
    );
  }
  if (!object_url) {
    return (
      <div
        className={`flex items-center justify-center bg-muted text-muted-foreground ${className ?? ''}`}
        data-testid={testid}
      >
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }
  return <img src={object_url} alt={alt} className={className} data-testid={testid} loading="lazy" />;
}
